import { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Plus,
  Trash,
  Tag,
  Hash,
  TextT,
  LinkSimple,
  Calendar,
  DotsSixVertical,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const TYPE_OPTIONS = [
  { key: "text", label: "Text", icon: TextT, hint: "Free text (e.g. task, subtask, notes)" },
  { key: "enum", label: "Enum / Tag", icon: Tag, hint: "Pick from a list (e.g. workstream)" },
  { key: "int", label: "Number", icon: Hash, hint: "Whole numbers (e.g. priority, effort)" },
  { key: "dependency", label: "Dependency", icon: LinkSimple, hint: "Reference other tasks by #" },
];

export default function ColumnsDialog({ open, onOpenChange, project, canEdit, tasks, onSave }) {
  const [dateCols, setDateCols] = useState([]);
  const [customCols, setCustomCols] = useState([]);
  const [workstreamColors, setWorkstreamColors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && project) {
      setDateCols(JSON.parse(JSON.stringify(project.date_columns || [])));
      setCustomCols(JSON.parse(JSON.stringify(project.custom_columns || [])));
      setWorkstreamColors({ ...(project.workstream_colors || {}) });
    }
  }, [open, project]);

  // All workstreams currently in use (derived from tasks) + any that already have a saved colour.
  const allWorkstreams = useMemo(() => {
    const set = new Set();
    (tasks || []).forEach((t) => { if (t.workstream) set.add(t.workstream); });
    Object.keys(workstreamColors || {}).forEach((k) => set.add(k));
    return Array.from(set).sort();
  }, [tasks, workstreamColors]);

  const addDateCol = () => {
    const id = `col_${Date.now().toString(36)}`;
    setDateCols([...dateCols, { id, label: "New Date", color: "#64748b", shape: "flag", pair_with: null }]);
  };
  const removeDateCol = (id) => setDateCols(dateCols.filter((c) => c.id !== id));
  const updateDateCol = (id, patch) => setDateCols(dateCols.map((c) => (c.id === id ? { ...c, ...patch } : c)));

  const addCustomCol = () => {
    const id = `cf_${Date.now().toString(36)}`;
    setCustomCols([...customCols, { id, label: "New Column", type: "text", options: [] }]);
  };
  const removeCustomCol = (id) => setCustomCols(customCols.filter((c) => c.id !== id));
  const updateCustomCol = (id, patch) => setCustomCols(customCols.map((c) => (c.id === id ? { ...c, ...patch } : c)));

  const setWorkstreamColor = (name, hex) => {
    setWorkstreamColors((prev) => ({ ...prev, [name]: hex }));
  };
  const clearWorkstreamColor = (name) => {
    setWorkstreamColors((prev) => {
      const next = { ...prev };
      delete next[name];
      return next;
    });
  };

  const save = async () => {
    // Validate
    const allIds = [...dateCols.map((c) => c.id), ...customCols.map((c) => c.id)];
    if (new Set(allIds).size !== allIds.length) {
      toast.error("Column IDs must be unique");
      return;
    }
    if (dateCols.some((c) => !c.label.trim()) || customCols.some((c) => !c.label.trim())) {
      toast.error("Every column needs a label");
      return;
    }
    setSaving(true);
    try {
      await onSave({
        date_columns: dateCols,
        custom_columns: customCols,
        workstream_colors: workstreamColors,
      });
      onOpenChange(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-md max-w-3xl p-0 gap-0 overflow-hidden" data-testid="columns-dialog">
        <DialogHeader className="p-6 pb-4 border-b border-slate-200">
          <DialogTitle className="font-display text-2xl font-black tracking-tight">Manage Columns</DialogTitle>
          <DialogDescription className="text-slate-500">
            Add, rename, or remove columns. Pick a type so the data is stored and displayed correctly.
          </DialogDescription>
        </DialogHeader>

        <div className="p-6 max-h-[70vh] overflow-y-auto custom-scrollbar space-y-6">
          {/* Date columns */}
          <section>
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="text-sm font-semibold text-slate-950">Date columns</div>
                <div className="text-xs text-slate-500 mt-0.5">
                  Rendered as bars or markers on the Gantt. Pair two to form a duration bar.
                </div>
              </div>
              {canEdit && (
                <Button size="sm" onClick={addDateCol} className="rounded-md bg-slate-950 hover:bg-slate-800 text-white" data-testid="add-date-col-btn">
                  <Plus size={12} weight="bold" /><span className="ml-1.5">Add date column</span>
                </Button>
              )}
            </div>
            <div className="space-y-2">
              {dateCols.map((c) => (
                <div key={c.id} className="flex items-center gap-2 p-2 border border-slate-200 rounded-md bg-white" data-testid={`date-col-row-${c.id}`}>
                  <DotsSixVertical size={14} weight="bold" className="text-slate-400 shrink-0" />
                  <Input
                    value={c.label}
                    onChange={(e) => updateDateCol(c.id, { label: e.target.value })}
                    disabled={!canEdit}
                    className="h-8 rounded-md border-slate-300 text-sm flex-1"
                  />
                  <Select value={c.shape} onValueChange={(v) => updateDateCol(c.id, { shape: v })} disabled={!canEdit}>
                    <SelectTrigger className="h-8 w-[100px] rounded-md border-slate-300 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="rounded-md">
                      <SelectItem value="bar">Bar</SelectItem>
                      <SelectItem value="diamond">Diamond</SelectItem>
                      <SelectItem value="flag">Flag</SelectItem>
                      <SelectItem value="dot">Dot</SelectItem>
                    </SelectContent>
                  </Select>
                  <input
                    type="color"
                    value={c.color}
                    onChange={(e) => updateDateCol(c.id, { color: e.target.value })}
                    disabled={!canEdit}
                    className="h-8 w-10 border border-slate-300 rounded-md cursor-pointer"
                  />
                  <Select
                    value={c.pair_with || "__none__"}
                    onValueChange={(v) => updateDateCol(c.id, { pair_with: v === "__none__" ? null : v })}
                    disabled={!canEdit}
                  >
                    <SelectTrigger className="h-8 w-[140px] rounded-md border-slate-300 text-xs">
                      <SelectValue placeholder="No pair" />
                    </SelectTrigger>
                    <SelectContent className="rounded-md">
                      <SelectItem value="__none__">No pair (point)</SelectItem>
                      {dateCols.filter((d) => d.id !== c.id).map((d) => (
                        <SelectItem key={d.id} value={d.id}>Pair with {d.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {canEdit && (
                    <button
                      type="button"
                      onClick={() => removeDateCol(c.id)}
                      className="h-8 w-8 rounded-md border border-slate-200 hover:border-red-300 hover:bg-red-50 hover:text-red-600 text-slate-500 flex items-center justify-center"
                      data-testid={`remove-date-col-${c.id}`}
                    >
                      <Trash size={12} weight="bold" />
                    </button>
                  )}
                </div>
              ))}
              {dateCols.length === 0 && (
                <div className="text-xs text-slate-500 text-center py-4 border border-dashed border-slate-200 rounded-md">
                  No date columns. Add one to render bars on the timeline.
                </div>
              )}
            </div>
          </section>

          {/* Custom columns */}
          <section>
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="text-sm font-semibold text-slate-950">Custom columns</div>
                <div className="text-xs text-slate-500 mt-0.5">
                  Add structured fields per task. Pick text, enum, number, or a dependency reference.
                </div>
              </div>
              {canEdit && (
                <Button size="sm" onClick={addCustomCol} className="rounded-md bg-slate-950 hover:bg-slate-800 text-white" data-testid="add-custom-col-btn">
                  <Plus size={12} weight="bold" /><span className="ml-1.5">Add custom column</span>
                </Button>
              )}
            </div>
            <div className="space-y-2">
              {customCols.map((c) => {
                const typeMeta = TYPE_OPTIONS.find((t) => t.key === c.type) || TYPE_OPTIONS[0];
                const Icon = typeMeta.icon;
                return (
                  <div key={c.id} className="border border-slate-200 rounded-md bg-white p-2" data-testid={`custom-col-row-${c.id}`}>
                    <div className="flex items-center gap-2">
                      <DotsSixVertical size={14} weight="bold" className="text-slate-400 shrink-0" />
                      <Input
                        value={c.label}
                        onChange={(e) => updateCustomCol(c.id, { label: e.target.value })}
                        disabled={!canEdit}
                        className="h-8 rounded-md border-slate-300 text-sm flex-1"
                      />
                      <Select value={c.type} onValueChange={(v) => updateCustomCol(c.id, { type: v, options: v === "enum" ? c.options : [] })} disabled={!canEdit}>
                        <SelectTrigger className="h-8 w-[150px] rounded-md border-slate-300 text-xs">
                          <span className="flex items-center gap-2"><Icon size={12} weight="bold" /><SelectValue /></span>
                        </SelectTrigger>
                        <SelectContent className="rounded-md">
                          {TYPE_OPTIONS.map((t) => (
                            <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {canEdit && (
                        <button
                          type="button"
                          onClick={() => removeCustomCol(c.id)}
                          className="h-8 w-8 rounded-md border border-slate-200 hover:border-red-300 hover:bg-red-50 hover:text-red-600 text-slate-500 flex items-center justify-center"
                          data-testid={`remove-custom-col-${c.id}`}
                        >
                          <Trash size={12} weight="bold" />
                        </button>
                      )}
                    </div>
                    {c.type === "enum" && (
                      <div className="mt-2 ml-6">
                        <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-ibm">Options (comma-separated)</Label>
                        <Input
                          value={(c.options || []).join(", ")}
                          onChange={(e) => updateCustomCol(c.id, { options: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}
                          disabled={!canEdit}
                          placeholder="ERP, Integration, QA, Finance"
                          className="h-8 mt-1 rounded-md border-slate-300 text-xs"
                        />
                      </div>
                    )}
                    <div className="ml-6 mt-1 text-[11px] text-slate-500">{typeMeta.hint}</div>
                  </div>
                );
              })}
              {customCols.length === 0 && (
                <div className="text-xs text-slate-500 text-center py-4 border border-dashed border-slate-200 rounded-md">
                  No custom columns yet.
                </div>
              )}
            </div>
          </section>

          {/* Workstream colours */}
          <section>
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="text-sm font-semibold text-slate-950">Workstream colours</div>
                <div className="text-xs text-slate-500 mt-0.5">
                  Pick a colour for each workstream so your team recognises it at a glance.
                  Blank = auto-coloured from a default palette.
                </div>
              </div>
            </div>
            <div className="space-y-2">
              {allWorkstreams.map((name) => {
                const hex = workstreamColors[name] || "";
                return (
                  <div
                    key={name}
                    className="flex items-center gap-2 p-2 border border-slate-200 rounded-md bg-white"
                    data-testid={`workstream-color-row-${name}`}
                  >
                    <span
                      className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-[11px] font-medium flex-1 min-w-0 truncate"
                      style={hex
                        ? { background: `${hex}22`, color: hex }
                        : { background: "#f1f5f9", color: "#475569" }}
                    >
                      <span
                        className="h-1.5 w-1.5 rounded-full shrink-0"
                        style={{ background: hex || "#94a3b8" }}
                      />
                      {name}
                    </span>
                    <input
                      type="color"
                      value={hex || "#64748b"}
                      onChange={(e) => setWorkstreamColor(name, e.target.value)}
                      disabled={!canEdit}
                      className="h-8 w-12 border border-slate-300 rounded-md cursor-pointer"
                      data-testid={`workstream-color-input-${name}`}
                    />
                    {hex && canEdit && (
                      <button
                        type="button"
                        onClick={() => clearWorkstreamColor(name)}
                        className="h-8 px-2 rounded-md border border-slate-200 text-[10px] font-mono-ibm uppercase tracking-widest text-slate-500 hover:text-slate-950 hover:border-slate-400"
                        data-testid={`workstream-color-reset-${name}`}
                      >
                        Reset
                      </button>
                    )}
                  </div>
                );
              })}
              {allWorkstreams.length === 0 && (
                <div className="text-xs text-slate-500 text-center py-4 border border-dashed border-slate-200 rounded-md">
                  No workstreams used yet. Assign a workstream to a task to set its colour.
                </div>
              )}
            </div>
          </section>
        </div>

        <DialogFooter className="p-6 pt-4 border-t border-slate-200 bg-slate-50">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-md border-slate-300">Cancel</Button>
          {canEdit && (
            <Button onClick={save} disabled={saving} className="rounded-md bg-slate-950 hover:bg-slate-800 text-white" data-testid="columns-save-btn">
              {saving ? "Saving…" : "Save columns"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
